import React from 'react';
import type { CentralAgentPerformance } from '../../services/MendsVisionService';
import { formatRelative } from '../../lib/centralFormat';

const HEALTH_ICON: Record<CentralAgentPerformance['health'], string> = {
  ok: '●',
  attention: '▲',
  idle: '○',
  paused: '‖',
};

function seconds(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

/**
 * Saúde e carga de cada funcionário digital, com os fatos que explicam o selo. O mesmo componente aparece na
 * Central e na tela Funcionários, com o mesmo cálculo do backend (GET central/overview).
 */
export const CentralAgentCards: React.FC<{
  agents: CentralAgentPerformance[];
  showSector?: boolean;
  periodLabel?: string;
  onOpen: (path: string) => void;
}> = ({ agents, showSector = false, periodLabel, onOpen }) => {
  if (agents.length === 0) {
    return <p className="mai-ci-empty">Nenhum funcionário digital cadastrado nesta conexão.</p>;
  }
  return (
    <ul className="mai-ci-agents">
      {agents.map((a) => {
        const delta = a.conversations - a.previousConversations;
        return (
          <li key={a.agentId} className={`mai-ci-agent is-${a.health}`}>
            <div className="mai-ci-agent-head">
              <div>
                <strong>{a.name}</strong>
                <small>
                  {showSector ? `${a.sectorName} · ` : ''}
                  {a.roles.length > 0 ? a.roles.join(' · ') : 'Sem função ligada'}
                </small>
              </div>
              <span className={`mai-ci-health is-${a.health}`}>
                <span aria-hidden>{HEALTH_ICON[a.health]}</span> {a.healthLabel}
              </span>
            </div>
            <dl className="mai-ci-agent-stats">
              <div>
                <dt>Conversas</dt>
                <dd>
                  {a.conversations}
                  <small title="Diferença para o período anterior">
                    {delta === 0 ? ' =' : delta > 0 ? ` +${delta}` : ` ${delta}`}
                  </small>
                </dd>
              </div>
              <div><dt>Turnos</dt><dd>{a.turns}</dd></div>
              <div><dt>Reuniões marcadas</dt><dd>{a.meetingsBooked}</dd></div>
              <div><dt>Transferências</dt><dd>{a.transfers}</dd></div>
              <div><dt>Sem resposta</dt><dd className={a.emptyTurns > 0 ? 'is-bad' : ''}>{a.emptyTurns}</dd></div>
              <div><dt title="Tempo que a IA leva para gerar cada resposta (mediana; 90% das respostas saem abaixo do segundo número)">Tempo para gerar resposta</dt><dd>{seconds(a.latencyP50Seconds)}<small> · 90%: {seconds(a.latencyP90Seconds)}</small></dd></div>
            </dl>
            {a.healthNotes.length > 0 && (
              <ul className="mai-ci-agent-notes">
                {a.healthNotes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            )}
            <div className="mai-ci-agent-foot">
              <span>
                {a.lastActivityAt ? `Último atendimento ${formatRelative(a.lastActivityAt)}` : 'Nunca atendeu'}
                {periodLabel ? ` · números de ${periodLabel.toLowerCase()}` : ''}
              </span>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={() => onOpen(a.editPath)}>
                Configurar
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
